import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { affiliateService } from '../../services/affiliateService';

interface ApprovalItem {
  affiliate_id: string;
  email?: string;
  name?: string;
  website?: string;
  created_at?: string;
  status: 'pending' | 'approved' | 'rejected';
}

interface ApprovalsQueueProps {
  programId?: string;
}

const ApprovalsQueue: React.FC<ApprovalsQueueProps> = ({ programId }) => {
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [approvals, setApprovals] = useState<ApprovalItem[]>([]);

  const fetchApprovals = async () => {
    if (!programId) {
      setApprovals([]);
      setError('No program ID provided');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await affiliateService.getApprovals(programId, statusFilter);
      setApprovals(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load approvals');
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, statusFilter]);

  const handleApprove = async (affiliateId: string) => {
    try {
      await affiliateService.setApprovalStatus(affiliateId, 'approved');
      toast.success('Approved');
      fetchApprovals();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to approve');
    }
  };

  const handleReject = async (affiliateId: string) => {
    try {
      const reason = prompt('Optional: reason for rejection') || '';
      await affiliateService.setApprovalStatus(affiliateId, 'rejected', undefined, reason);
      toast('Rejected', { icon: '⚠️' });
      fetchApprovals();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to reject');
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Approvals</h2>
          <p className="text-sm text-gray-500">Review and act on affiliate applications.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Status:</label>
          <select
            className="border rounded px-3 py-2"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading approvals...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : approvals.length === 0 ? (
        <p className="text-gray-500">No items.</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-left text-sm text-gray-600">
                <th className="py-3 px-3">Affiliate</th>
                <th className="py-3 px-3">Email</th>
                <th className="py-3 px-3">Website</th>
                <th className="py-3 px-3">Applied</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {approvals.map((a) => (
                <tr key={a.affiliate_id} className="border-b border-gray-100 text-sm">
                  <td className="py-3 px-3">{a.name || a.affiliate_id}</td>
                  <td className="py-3 px-3">{a.email || '-'}</td>
                  <td className="py-3 px-3">{a.website || '-'}</td>
                  <td className="py-3 px-3">{a.created_at ? new Date(a.created_at).toLocaleString() : '-'}</td>
                  <td className="py-3 px-3">{a.status}</td>
                  <td className="py-3 px-3 flex gap-2">
                    {a.status === 'pending' ? (
                      <>
                        <button className="text-green-700 hover:underline" onClick={() => handleApprove(a.affiliate_id)}>Approve</button>
                        <button className="text-red-700 hover:underline" onClick={() => handleReject(a.affiliate_id)}>Reject</button>
                      </>
                    ) : (
                      <span className="text-gray-500">No actions</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ApprovalsQueue;
